// THE WALL TOOL (3.20.0). Owner, 2026-10-21: "a wall drawing tool for easy layout of walls".
//   WALLS mode: click point after point, as the Line tool. Type a length (or length<angle) and press Enter for an exact
//     wall. Shift keeps a wall square. Click the first point to close a room. Enter, a double-click or a right-click
//     ends the run. Backspace takes the last point away. Esc stops.
//   ROOM mode: click one corner, then the opposite corner, or type width,depth and press Enter: four walls, closed.
// The thickness, the line you draw (the centre or a face of the wall), the mode and the fill are remembered (prefs.wall)
// and set in the Inspector while the tool is on. The walls are made by walls/wall.ts makeWallCommand.
import { angleOf, dir, dist, sub, type Vec } from "../core/geometry";
import { formatLength, parseLengthAngle, parsePair } from "../core/units";
import type { FillMode } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld } from "../engine/renderEntities";
import { DEFAULT_WALL, WALL_LINE, makeWallCommand, wallGeometry, type WallJustify } from "../walls/wall";
import { wallAttachPoint } from "../walls/edit";

/** The faces of the wall being drawn: a light blue line. */
const PREVIEW = { ...WALL_LINE, strokeColor: "#4dd0e1" };
import { drawReadout } from "./line";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

/** The Wall tool's remembered settings. */
export interface WallPrefs {
  thickness: number;
  justify: WallJustify;
  mode: "walls" | "room";
  fill: FillMode;
}
export const DEFAULT_WALL_PREFS: WallPrefs = { thickness: DEFAULT_WALL.thickness, justify: "centre", mode: "walls", fill: "hatch" };

export class WallTool implements Tool {
  readonly id = "wall" as const;
  private points: Vec[] = [];
  private cursor: Vec | null = null;
  /** 3.23.0: where the pointer attaches to another wall (its free end, or its centre line), or null */
  private attach: ReturnType<typeof wallAttachPoint> = null;
  /** the step prompt at the pointer, when nothing attaches */
  private tip: string | null = null;
  /** the id of the last wall made, for the post-commit length re-edit */
  lastWallId: string | null = null;

  constructor(private ctx: ToolCtx) {}

  private settings(): WallPrefs {
    return this.ctx.prefs().wall ?? DEFAULT_WALL_PREFS;
  }

  activate(): void {
    this.reset();
  }
  deactivate(): void {
    this.reset();
  }
  cancel(): void {
    this.reset();
  }
  inProgress(): boolean {
    return this.points.length > 0;
  }

  /**
   * 3.21.0: in Room mode the second click is the opposite corner. ORTHO does not hold it on a line from the first
   * corner. Owner, 2026-10-21: "tried to use the room drawing tool and I couldn't set a rectangle" (ORTHO was on).
   */
  ignoresOrtho(): boolean {
    return this.settings().mode === "room" && this.points.length === 1;
  }

  /** The Inspector changed the mode: start again. */
  modeChanged(): void {
    this.reset();
  }

  private reset(): void {
    this.points = [];
    this.cursor = null;
    this.ctx.setSnapBase(null);
    this.ctx.setSelfClose(null);
    const room = this.settings().mode === "room";
    this.ctx.setMeasure(room ? "none" : "length", room ? "" : "length or length<angle");
    this.ctx.setHint(room ? "Wall · room: click one corner · Esc = stop" : "Wall: click the start of the wall · Esc = stop");
    this.ctx.setCursorTip(null);
    this.tip = null;
    this.attach = null;
    this.ctx.requestRender();
  }

  /**
   * 3.23.0: a point near another wall goes onto it: the free end of a run (the two then meet in a mitre) or the centre
   * line of a wall (a T). Owner, 2026-10-21: "walls can attach to other walls and the intersection needs to look neat".
   */
  private onWalls(p: Vec): Vec {
    this.attach = wallAttachPoint(this.ctx.store.doc, p, 12 / this.ctx.scale());
    this.ctx.setCursorTip(this.attach ? (this.attach.kind === "end" ? "joins the end of the wall" : "joins the wall (a T)") : this.tip);
    return this.attach?.p ?? p;
  }

  private syncState(): void {
    const n = this.points.length;
    const room = this.settings().mode === "room";
    this.ctx.setSnapBase(n > 0 ? this.points[n - 1]! : null);
    this.ctx.setSelfClose(!room && n >= 3 ? this.points[0]! : null);
    if (room) {
      this.ctx.setMeasure(n ? "pair" : "none", n ? "width,depth" : "");
      this.ctx.setHint(n ? "Wall · room: click the opposite corner, or type width,depth ⏎ · Esc = stop" : "Wall · room: click one corner · Esc = stop");
      this.tip = n ? "opposite corner · w,d ⏎" : null;
      this.ctx.setCursorTip(this.tip);
    } else if (n === 0) {
      this.ctx.setHint("Wall: click the start of the wall · a click near another wall joins it · Esc = stop");
      this.tip = null;
      this.ctx.setCursorTip(null);
    } else if (n < 3) {
      this.ctx.setHint("Wall: click the next corner, or type a length ⏎ · Shift = square · Enter or right-click = end");
      this.tip = "next corner · length ⏎";
      this.ctx.setCursorTip(this.tip);
    } else {
      this.ctx.setHint("Wall: click the next corner · click the first point to close the room · Enter = end · Backspace = back one");
      this.tip = "next corner · start = close · ⏎";
      this.ctx.setCursorTip(this.tip);
    }
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button === 2) {
      this.finish(false);
      return;
    }
    if (e.button !== 0) return;
    this.addPoint(this.onWalls(e.point));
  }

  private addPoint(p: Vec): void {
    if (this.settings().mode === "room") {
      if (!this.points.length) {
        this.points.push(p);
        this.syncState();
        return;
      }
      this.room(this.points[0]!, p);
      return;
    }
    const last = this.points[this.points.length - 1];
    if (this.points.length >= 3 && dist(p, this.points[0]!) < 1e-9) {
      this.finish(true);
      return;
    }
    if (last && dist(p, last) < 1e-9) return;
    this.points.push(p);
    this.syncState();
  }

  private room(a: Vec, b: Vec): void {
    if (Math.abs(b.x - a.x) < 1e-9 || Math.abs(b.y - a.y) < 1e-9) return;
    this.points = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
    this.finish(true);
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = this.onWalls(e.point);
    this.ctx.requestRender();
  }
  pointerUp(): void {}
  doubleClick(): void {
    this.finish(false);
  }

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Enter") {
      this.finish(false);
      return true;
    }
    if ((e.key === "Backspace" || e.key === "Delete") && this.points.length > 0) {
      this.points.pop();
      this.syncState();
      return true;
    }
    if (e.key === "Escape" && this.points.length > 0) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    const { units } = this.ctx.docUnits();
    if (!this.points.length) return false;
    const last = this.points[this.points.length - 1]!;
    if (this.settings().mode === "room") {
      const pair = parsePair(raw, units);
      if (!pair || Math.abs(pair[0]) < 1e-9 || Math.abs(pair[1]) < 1e-9) return false;
      const sx = this.cursor && this.cursor.x < last.x ? -1 : 1;
      const sy = this.cursor && this.cursor.y < last.y ? -1 : 1;
      this.room(last, { x: last.x + sx * Math.abs(pair[0]), y: last.y + sy * Math.abs(pair[1]) });
      return true;
    }
    const parsed = parseLengthAngle(raw, units);
    if (!parsed) return false;
    let d: Vec;
    if (parsed.angleDeg !== null) d = dir(parsed.angleDeg);
    else {
      const toward = this.cursor && dist(this.cursor, last) > 1e-9 ? this.cursor : null;
      d = toward ? dir(angleOf(sub(toward, last))) : dir(0);
    }
    this.addPoint({ x: last.x + d.x * parsed.length, y: last.y + d.y * parsed.length });
    return true;
  }

  private finish(closed: boolean): void {
    const pts = this.points;
    if (pts.length < 2 || (closed && pts.length < 3)) {
      this.reset();
      return;
    }
    const s = this.settings();
    const made = makeWallCommand(this.ctx.store.doc, this.ctx.activeLayerId(), pts, closed, {
      thickness: s.thickness,
      justify: s.justify,
      fill: { ...DEFAULT_WALL.fill, mode: s.fill },
      doors: []
    });
    if (made) {
      this.ctx.store.dispatch(made.cmd);
      this.lastWallId = made.groupId;
      this.ctx.setHint(`Wall drawn: ${pts.length - (closed ? 0 : 1)} ${pts.length - (closed ? 0 : 1) === 1 ? "wall" : "walls"}${closed ? ", a room" : ""}. Right-click a wall (Select tool) to add a door.`);
    }
    this.points = [];
    this.cursor = null;
    this.ctx.setSnapBase(null);
    this.ctx.setSelfClose(null);
    this.ctx.setCursorTip(null);
    this.ctx.setMeasure(s.mode === "room" ? "none" : "length", s.mode === "room" ? "" : "length or length<angle");
    this.ctx.requestRender();
  }

  renderDynamic(v: RenderView): void {
    // 3.23.0: the point where the pointer joins another wall
    if (this.attach) {
      const { ctx } = v;
      const r = 6 / v.cam.scale;
      ctx.save();
      ctx.strokeStyle = PREVIEW.strokeColor;
      ctx.lineWidth = 2 / v.cam.scale;
      ctx.setLineDash([]);
      ctx.strokeRect(this.attach.p.x - r, this.attach.p.y - r, 2 * r, 2 * r);
      ctx.restore();
    }
    if (!this.points.length) return;
    const s = this.settings();
    let pts: Vec[];
    let closed = false;
    if (s.mode === "room") {
      if (!this.cursor) return;
      const a = this.points[0]!;
      const b = this.cursor;
      if (Math.abs(b.x - a.x) < 1e-9 || Math.abs(b.y - a.y) < 1e-9) return;
      pts = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
      closed = true;
    } else {
      pts = this.cursor && dist(this.cursor, this.points[this.points.length - 1]!) > 1e-9 ? [...this.points, this.cursor] : [...this.points];
    }
    const geo = wallGeometry(pts, closed, { thickness: s.thickness, justify: s.justify });
    const { ctx } = v;
    ctx.save();
    ctx.strokeStyle = PREVIEW.strokeColor;
    ctx.lineWidth = strokeWidthWorld(PREVIEW, v.cam, v.dpr);
    ctx.setLineDash([]);
    if (geo) {
      for (const F of [geo.FL, geo.FR]) {
        ctx.beginPath();
        F.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        if (geo.closed) ctx.closePath();
        ctx.stroke();
      }
      if (!geo.closed) {
        ctx.beginPath();
        ctx.moveTo(geo.FL[0]!.x, geo.FL[0]!.y);
        ctx.lineTo(geo.FR[0]!.x, geo.FR[0]!.y);
        const k = geo.FL.length - 1;
        ctx.moveTo(geo.FL[k]!.x, geo.FL[k]!.y);
        ctx.lineTo(geo.FR[k]!.x, geo.FR[k]!.y);
        ctx.stroke();
      }
    }
    ctx.restore();
    if (this.cursor) {
      const { units, precision } = this.ctx.docUnits();
      const last = this.points[this.points.length - 1]!;
      if (s.mode === "room") {
        const w = Math.abs(this.cursor.x - last.x);
        const h = Math.abs(this.cursor.y - last.y);
        drawReadout(v, this.cursor, `${formatLength(w, units, precision)} × ${formatLength(h, units, precision)}`);
      } else {
        const len = dist(this.cursor, last);
        if (len > 1e-9) drawReadout(v, this.cursor, `${formatLength(len, units, precision)}  ${angleOf(sub(this.cursor, last)).toFixed(1)}°`);
      }
    }
  }
}
